import React, { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { useT } from "@/i18n/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { FACTORY_SHORT, WAREHOUSE_FLAG, WAREHOUSE_LABEL, WAREHOUSE_STATION_LABEL, pickupInfo } from "@/constants/warehouses";

// Station step for a warehouse pick-up: "Product found" (stock −1, continue) or
// "Not found" (count → 0, order back to Advance as PRODUCTION NEEDED).
export function PickupCard({ order, mySignature, canConfirm, compact = false, onNotFound }) {
  const { t } = useT();
  const qc = useQueryClient();
  const [note, setNote] = useState("");
  const [askNotFound, setAskNotFound] = useState(false);
  const p = pickupInfo(order) || {};
  const wh = p.warehouse || "china";
  const whLabel = WAREHOUSE_LABEL[wh] || wh;
  const result = useMutation({
    mutationFn: async (found) => (await api.post(`/orders/${order.id}/pickup-result`, { found, employee_signature: mySignature, note: note.trim() })).data,
    onSuccess: (r, found) => {
      qc.invalidateQueries();
      if (found) toast.success(r.stock_deducted ? t("Product found — stock −1. Continue.") : t("Product found (no stock record to deduct). Continue."));
      else { toast.warning(`${order.po_number} → ${t("Advance Orders")} · ${t("PRODUCTION NEEDED")}`); onNotFound?.(); }
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not save"),
  });

  const Cell = ({ label, children, testid }) => (
    <div className="rounded-md border border-amber-300 bg-white p-3">
      <div className="text-[11px] uppercase tracking-widest text-slate-500">{label}</div>
      <div className={`font-black break-all ${compact ? "text-xl" : "text-2xl lg:text-3xl"}`} data-testid={testid}>{children}</div>
    </div>
  );

  return (
    <div className={compact ? "" : "max-w-2xl mx-auto py-6"} data-testid="pickup-card">
      <div className={`border-2 border-amber-600 bg-amber-50 rounded-lg ${compact ? "p-4" : "p-8 text-center"}`}>
        {!compact && <div className="text-5xl mb-3">📦</div>}
        <div className={`${compact ? "text-lg" : "text-2xl"} font-bold text-amber-900`}>
          {t("Pick up the part")} · {WAREHOUSE_FLAG[wh]} {whLabel} {t("warehouse")}{compact ? ` · ${order.po_number}` : ""}
        </div>
        <div className="text-sm text-amber-800 mt-1">{t("Find the item in storage and check it matches the catalog number, colour and factory below.")}</div>
        <div className={`grid ${compact ? "grid-cols-2 md:grid-cols-6" : "grid-cols-2 md:grid-cols-3"} gap-3 mt-4 text-left`}>
          <Cell label={t("Catalog #")} testid="pickup-cat"><span className="font-mono">{order.catalog_number || "—"}</span></Cell>
          <Cell label={t("Color")} testid="pickup-color">
            <span className="uppercase">{p.color || order.color || "—"}</span>
            {p.color_override && (
              <div className="text-[11px] font-bold text-amber-800 bg-amber-100 border border-amber-300 rounded px-1.5 py-0.5 mt-1 inline-block" data-testid="pickup-color-override">
                ⚠ {t("different colour")} — {t("ordered")} {order.color || "—"} · {t("approved by")} {p.override_signature || "—"}
              </div>
            )}
            {p.from_no_color && (
              <div className="text-[11px] font-bold text-teal-800 bg-teal-50 border border-teal-300 rounded px-1.5 py-0.5 mt-1 inline-block" data-testid="pickup-from-no-color">
                ◌ {t("take from the NO COLOR stock")} — {t("usable for any colour")}
              </div>
            )}
          </Cell>
          <Cell label={t("Bin / shelf")} testid="pickup-bin"><span className="font-mono">{p.bin || "—"}</span></Cell>
          <Cell label={t("Factory codes")} testid="pickup-factory">
            <span className={`inline-flex items-center rounded px-2 ${p.factory === "factory_2" ? "bg-neutral-800 text-white" : "bg-neutral-100 text-neutral-900"}`}>{FACTORY_SHORT[p.factory] || "F1"}</span>
            <span className="text-xs font-normal text-slate-500 ml-2">{p.factory === "factory_2" ? t("white number") : t("black number")}</span>
          </Cell>
          <Cell label={t("Spacers")}><span className="text-lg">{order.spacer || "—"} × {order.spacer_qty || "—"}</span></Cell>
          <Cell label={t("Sent by")}><span className="text-sm font-semibold">{p.sent_by || "—"}{p.stock_at_send != null ? ` · ${p.stock_at_send} ${t("in stock")}` : ""}</span></Cell>
        </div>
      </div>
      {canConfirm ? (
        <div className="mt-4 space-y-3">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="text-xs rounded-md border border-slate-300 bg-white flex items-center px-3 h-10 font-mono text-slate-800" data-testid="pickup-sig">
              <span className="text-slate-400 text-[11px] uppercase tracking-widest mr-2">{t("Signed as")}</span><span className="font-semibold">{mySignature}</span>
            </div>
            <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder={t("Note (optional) — shelf / bin, or why it was not found")} className="flex-1 min-w-[220px]" data-testid="pickup-note" />
          </div>
          <div className={`grid ${compact ? "grid-cols-2" : "grid-cols-1 md:grid-cols-2"} gap-3`}>
            <Button data-testid="pickup-found-btn" disabled={result.isPending} onClick={() => result.mutate(true)} className={`bg-emerald-600 hover:bg-emerald-700 text-white ${compact ? "h-11" : "h-14 text-lg"}`}>
              {result.isPending ? t("Saving…") : `✓ ${t("PRODUCT FOUND")}`}
            </Button>
            {askNotFound ? (
              <div className="flex gap-2">
                <Button data-testid="pickup-notfound-confirm" disabled={result.isPending} onClick={() => result.mutate(false)} className={`flex-1 bg-red-600 hover:bg-red-700 text-white ${compact ? "h-11" : "h-14"}`}>
                  {t("Confirm NOT FOUND → Production needed")}
                </Button>
                <Button variant="outline" onClick={() => setAskNotFound(false)} className={compact ? "h-11" : "h-14"} data-testid="pickup-notfound-cancel">{t("Back")}</Button>
              </div>
            ) : (
              <Button data-testid="pickup-notfound-btn" variant="outline" disabled={result.isPending} onClick={() => setAskNotFound(true)} className={`border-red-500 text-red-700 hover:bg-red-50 ${compact ? "h-11" : "h-14 text-lg"}`}>
                ✗ {t("NOT FOUND")}
              </Button>
            )}
          </div>
          <div className="text-[11px] text-slate-500">{t("Not found sets this colour's count in the")} {whLabel} {t("warehouse to 0 and sends the order back to Advance Orders with a PRODUCTION NEEDED alert.")}</div>
        </div>
      ) : (
        <div className="mt-4 text-sm text-slate-500 text-center" data-testid="pickup-wait">{t("Waiting for")} {WAREHOUSE_STATION_LABEL[wh]} {t("to pick up and confirm the part.")}</div>
      )}
    </div>
  );
}
